import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logout from "./Logout";
import { FaEnvelope, FaCoins, FaHome, FaUser, FaComment, FaUsers, FaBell, FaChartBar, FaDollarSign, FaUserShield } from "react-icons/fa"; // Thêm biểu tượng mới

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalMessages: 0,
    unreadMessages: 0,
    chatParticipants: 0,
    pointsBalance: 0,
  });
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { user, fetchWithRetry, isLoading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchWithRetry({
          method: "get",
          url: "/api/users/dashboard-stats",
        });
        setStats((prevStats) => ({ ...prevStats, ...response.data }));
      } catch (error) {
        console.error("Lỗi khi lấy thống kê:", error);
        setError(
          error.response?.data?.message || "Không thể tải thống kê. Vui lòng thử lại sau."
        );
        if (error.response?.status === 401 || error.response?.status === 403) {
          setTimeout(() => logout(), 3000);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchNotificationSettings = async () => {
      try {
        const response = await fetchWithRetry({
          method: "get",
          url: "/api/users/email-notifications",
        });
        setNotificationSettings(response.data);
      } catch (error) {
        console.error("Lỗi khi lấy cài đặt thông báo:", error);
        setError(
          error.response?.data?.message || "Không thể tải cài đặt thông báo. Vui lòng thử lại sau."
        );
      }
    };

    fetchStats();
    fetchNotificationSettings();
  }, [user, fetchWithRetry, isLoading, logout, navigate]);

  // Nếu đang tải dữ liệu từ AuthContext
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid"></div>
      </div>
    );
  }

  // Tính số lượng thông báo đã bật
  const enabledNotifications = notificationSettings
    ? Object.values(notificationSettings).filter((value) => value === true).length
    : 0;

  return (
    <div className="flex h-screen bg-gray-100 font-sans">
      {/* Sidebar */}
      <div className="w-64 bg-gray-800 text-white p-6 space-y-6 fixed h-full">
        <h2 className="text-2xl font-bold mb-6">Menu</h2>
        <nav className="space-y-2">
          <Link
            to="/homepage"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaHome className="text-lg" />
            <span>Trang chủ</span>
          </Link>
          <Link
            to="/profile"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaUser className="text-lg" />
            <span>Hồ sơ cá nhân</span>
          </Link>
          <Link
            to="/chat"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaComment className="text-lg" />
            <span>Chat</span>
          </Link>
          <Link
            to="/social"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaUsers className="text-lg" />
            <span>Kết nối xã hội</span>
          </Link>
          <Link
            to="/notifications"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaBell className="text-lg" />
            <span>Thông báo</span>
          </Link>
          <Link
            to="/vip"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaDollarSign className="text-lg" />
            <span>Điểm & VIP</span>
          </Link>
          <Link
            to="/coin-management"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaCoins className="text-lg" />
            <span>Quản lý Coin & Rút tiền</span>
          </Link>
          <Link
            to="/reports"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaChartBar className="text-lg" />
            <span>Báo cáo</span>
          </Link>
          <Link
            to="/settings"
            className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
          >
            <FaEnvelope className="text-lg" />
            <span>Cài đặt thông báo</span>
          </Link>
          {user?.role === "admin" && (
            <Link
              to="/admin/users"
              className="block py-2 px-4 rounded hover:bg-gray-700 flex items-center space-x-2"
            >
              <FaUserShield className="text-lg" />
              <span>Quản lý người dùng</span>
            </Link>
          )}
          <Logout className="mt-6 block" />
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <header className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              🎉 Xin chào, {user?.name || "Người dùng"}!
            </h1>
            <p className="text-gray-600">Email: {user?.email || "Chưa cập nhật"}</p>
            <p className="text-gray-600">
              Vai trò: <strong>{user?.role || "Chưa xác định"}</strong>
            </p>
          </div>
          <div className="text-right">
            <span className="text-lg font-semibold text-green-600">
              Điểm: {stats.pointsBalance}
            </span>
          </div>
        </header>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        {/* Thống kê và báo cáo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-lg font-semibold text-gray-700">Tổng tin nhắn</h3>
            <p className="text-3xl font-bold text-blue-600 mt-2">
              {loading ? "Đang tải..." : stats.totalMessages}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-lg font-semibold text-gray-700">Tin nhắn chưa đọc</h3>
            <p className="text-3xl font-bold text-red-600 mt-2">
              {loading ? "Đang tải..." : stats.unreadMessages}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-lg font-semibold text-gray-700">Người tham gia</h3>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {loading ? "Đang tải..." : stats.chatParticipants}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-lg font-semibold text-gray-700">Điểm thưởng</h3>
            <p className="text-3xl font-bold text-yellow-600 mt-2">
              {loading ? "Đang tải..." : stats.pointsBalance}
            </p>
          </div>
        </div>

        {/* Trạng thái thông báo email */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
            <FaEnvelope className="mr-2" /> Trạng thái thông báo email
          </h2>
          {notificationSettings ? (
            <div>
              <p className="text-gray-600 mb-2">
                Bạn đã bật{" "}
                <span className="font-semibold text-blue-600">{enabledNotifications}</span>{" "}
                loại thông báo email.
              </p>
              <p className="text-gray-600">
                {enabledNotifications > 0
                  ? "Bạn sẽ nhận thông báo qua email cho các sự kiện như tin nhắn mới, lượt thích, v.v."
                  : "Bạn chưa bật thông báo email nào. Hãy thiết lập để không bỏ lỡ thông tin quan trọng!"}
              </p>
              <Link
                to="/settings"
                className="mt-4 inline-block px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
              >
                Thiết lập thông báo
              </Link>
            </div>
          ) : (
            <p className="text-gray-600">Đang tải cài đặt thông báo...</p>
          )}
        </div>

        {/* Nội dung chính */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Tổng quan hoạt động</h2>
          <p className="text-gray-600">
            Chào mừng bạn đến với trang Dashboard! Tại đây, bạn có thể theo dõi các thống kê và
            quản lý hoạt động của mình. Sử dụng menu bên trái để điều hướng đến các tính năng khác
            như Chat, Kết nối xã hội, Thông báo, Điểm & VIP, Báo cáo, và Cài đặt.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;